import React from 'react';
import { Phone, Mail, MapPin, MessageCircle, Clock } from 'lucide-react';
import { mockData } from '../mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const handleWhatsApp = () => {
    const phoneNumber = mockData.contact.whatsapp;
    const message = encodeURIComponent('Hola, me gustaría más información sobre ERBA');
    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${message}`;
    
    const newWindow = window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      const link = document.createElement('a');
      link.href = whatsappUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-[#004534] text-white">
      {/* Main footer content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="flex items-center space-x-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_farmtools-repair/artifacts/rf4i8qai_logo_erba_transparente.png" 
                alt="ERBA Service - Taller de reparación de maquinaria agrícola y de jardín en Torrox" 
                className="h-12"
                loading="lazy"
              />
              <div>
                <h3 className="text-2xl font-bold">ERBA</h3>
                <p className="text-green-300 text-sm">Taller de maquinaria agrícola y jardín</p>
              </div>
            </div>
            <p className="text-green-100 leading-relaxed mb-6 max-w-md">
              Especialistas en mantenimiento y reparación de maquinaria agrícola y de jardín 
              en Torrox y toda la Axarquía. Servicio profesional con garantía completa.
            </p>
            <div className="flex space-x-4">
              <button 
                onClick={handleWhatsApp}
                className="bg-[#16a34a] hover:bg-[#15803d] p-3 rounded-full transition-colors"
                title="WhatsApp"
              >
                <MessageCircle className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-[#f97316]">Enlaces Rápidos</h4>
            <ul className="space-y-3">
              <li>
                <button 
                  onClick={() => scrollToSection('inicio')}
                  className="text-green-100 hover:text-white transition-colors flex items-center"
                >
                  Inicio
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('servicios')}
                  className="text-green-100 hover:text-white transition-colors flex items-center"
                >
                  Servicios
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('nosotros')}
                  className="text-green-100 hover:text-white transition-colors"
                >
                  Quiénes Somos
                </button>
              </li>
              <li>
                <button 
                  onClick={() => scrollToSection('contacto')}
                  className="text-green-100 hover:text-white transition-colors"
                >
                  Contacto
                </button>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-6 text-[#f97316]">Contacto</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <Phone className="h-4 w-4 text-[#16a34a] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-green-100 text-sm">Teléfono</p>
                  <p className="text-white font-medium">{mockData.contact.phone}</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="h-4 w-4 text-[#16a34a] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-green-100 text-sm">Email</p>
                  <p className="text-white font-medium break-all">{mockData.contact.email}</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="h-4 w-4 text-[#16a34a] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-green-100 text-sm">Dirección</p>
                  <p className="text-white font-medium">{mockData.contact.address}</p>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="h-4 w-4 text-[#16a34a] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-green-100 text-sm">Horarios</p>
                  <p className="text-white font-medium">{mockData.contact.hours}</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-green-800">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-green-100 text-sm text-center md:text-left">
              <p>&copy; {currentYear} ERBA – Taller de maquinaria agrícola y jardín. Todos los derechos reservados.</p>
            </div>
            <div className="flex flex-wrap justify-center md:justify-end space-x-4 md:space-x-6 text-sm">
              <button 
                onClick={() => scrollToSection('servicios')}
                className="text-green-100 hover:text-white transition-colors"
              >
                Servicios
              </button>
              <button 
                onClick={() => scrollToSection('contacto')}
                className="text-green-100 hover:text-white transition-colors"
              >
                Contacto
              </button>
              <a 
                href="/aviso-legal"
                className="text-green-100 hover:text-white transition-colors"
              >
                Aviso legal
              </a>
              <a 
                href="/politica-cookies"
                className="text-green-100 hover:text-white transition-colors"
              >
                Cookies
              </a>
              <a 
                href="/politica-privacidad"
                className="text-green-100 hover:text-white transition-colors"
              >
                Política de privacidad
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Floating WhatsApp button for mobile */}
      <button
        onClick={handleWhatsApp}
        className="fixed bottom-6 right-6 bg-[#16a34a] hover:bg-[#15803d] text-white p-4 rounded-full shadow-2xl z-50 md:hidden transition-all duration-300 transform hover:scale-110"
        title="Contactar por WhatsApp"
      >
        <MessageCircle className="h-6 w-6" />
      </button>
    </footer>
  );
};